/**
 * Compartment detection — enumerating the enclosed faces of a ship's line graph.
 *
 * This is the load-bearing piece of the ship model. "Surface area", which
 * components are protected, whether an internal thruster has been exposed to
 * space, and what counts as floating debris after damage all fall out of it.
 *
 * ── The algorithm ───────────────────────────────────────────────────────────
 *
 * Every line becomes two directed half-edges. At each joint the outgoing
 * half-edges are sorted by angle. To walk a face, arrive at joint V along
 * (U -> V), look up the reverse half-edge (V -> U) in V's angle-sorted list,
 * and leave along the entry immediately *before* it (i.e. the next one
 * clockwise). Repeating that until the walk returns to its starting half-edge
 * traces one face.
 *
 * With that rule, enclosed faces come out counter-clockwise (positive signed
 * area) and the outer boundary comes out clockwise (negative). That sign is
 * what separates "a compartment" from "the space around the ship", so no
 * special-casing of the outer face is needed — it identifies itself.
 *
 * Edges that are not part of any cycle (a spar sticking out into space) get
 * walked in both directions within the same face and contribute zero net area,
 * so they fall out via the same area test rather than needing their own pass.
 *
 * ── Cost ────────────────────────────────────────────────────────────────────
 *
 * O(E log E), dominated by the per-joint angle sort. Full recomputation is
 * cheap enough to run on every structural change at the scale of a single ship.
 * The incremental "only recompute the two faces a broken line separated"
 * optimisation described in the technical plan is deliberately not implemented
 * yet — do it only if profiling says the full recompute actually costs
 * something, since the incremental version is much easier to get subtly wrong.
 */
import { EPSILON, indexJoints, } from './types.js';
function halfEdgeKey(lineId, from) {
    return `${lineId}>${from}`;
}
/** Shoelace formula. Positive for counter-clockwise winding. */
function signedArea(points) {
    let sum = 0;
    for (let i = 0; i < points.length; i++) {
        const current = points[i];
        const next = points[(i + 1) % points.length];
        if (!current || !next)
            continue;
        sum += current.x * next.y - next.x * current.y;
    }
    return sum / 2;
}
/**
 * A stable identifier for a compartment, derived from its boundary rather than
 * assigned. Two recomputations of an unchanged compartment produce the same id,
 * so callers can keep component-to-compartment associations across recomputes.
 */
function compartmentIdFor(lineIds) {
    return [...lineIds].sort().join('|');
}
/** Walk every face of the graph, enclosed and outer alike. */
function traceFaces(graph) {
    const joints = indexJoints(graph);
    const outgoing = new Map();
    for (const joint of graph.joints) {
        outgoing.set(joint.id, []);
    }
    for (const line of graph.lines) {
        const a = joints.get(line.a);
        const b = joints.get(line.b);
        // Skip lines referencing missing joints, and degenerate self-loops or
        // zero-length lines, which have no meaningful direction to sort by.
        if (!a || !b || line.a === line.b)
            continue;
        if (Math.hypot(b.x - a.x, b.y - a.y) < EPSILON)
            continue;
        outgoing.get(line.a)?.push({
            lineId: line.id,
            from: line.a,
            to: line.b,
            angle: Math.atan2(b.y - a.y, b.x - a.x),
        });
        outgoing.get(line.b)?.push({
            lineId: line.id,
            from: line.b,
            to: line.a,
            angle: Math.atan2(a.y - b.y, a.x - b.x),
        });
    }
    const indexInList = new Map();
    for (const edges of outgoing.values()) {
        edges.sort((p, q) => p.angle - q.angle);
        edges.forEach((edge, i) => indexInList.set(halfEdgeKey(edge.lineId, edge.from), i));
    }
    const faces = [];
    const visited = new Set();
    for (const edges of outgoing.values()) {
        for (const start of edges) {
            const startKey = halfEdgeKey(start.lineId, start.from);
            if (visited.has(startKey))
                continue;
            const lineIds = [];
            const jointIds = [];
            const points = [];
            let current = start;
            while (current) {
                const key = halfEdgeKey(current.lineId, current.from);
                if (visited.has(key))
                    break;
                visited.add(key);
                lineIds.push(current.lineId);
                jointIds.push(current.from);
                const point = joints.get(current.from);
                if (point)
                    points.push({ x: point.x, y: point.y });
                // Leave the destination along the next half-edge clockwise from the one
                // we arrived on. `siblings` is the destination joint's angle-sorted list.
                const siblings = outgoing.get(current.to);
                const reverseIndex = indexInList.get(halfEdgeKey(current.lineId, current.to));
                if (!siblings || siblings.length === 0 || reverseIndex === undefined)
                    break;
                const nextIndex = (reverseIndex - 1 + siblings.length) % siblings.length;
                const next = siblings[nextIndex];
                if (!next)
                    break;
                current = halfEdgeKey(next.lineId, next.from) === startKey ? undefined : next;
            }
            if (lineIds.length > 0) {
                faces.push({ lineIds, jointIds, area: signedArea(points) });
            }
        }
    }
    return faces;
}
/**
 * Find every enclosed compartment in a ship.
 *
 * Returns only enclosed faces — the space outside the ship is not a
 * compartment. Faces are marked `exposed` when they share a boundary line with
 * the outside, which is what activates otherwise-dormant internal thrusters and
 * pilot seats.
 */
export function findCompartments(graph) {
    const faces = traceFaces(graph);
    const hullLineIds = outerLines(faces);
    return faces
        .filter((face) => face.area > EPSILON)
        .map((face) => ({
        id: compartmentIdFor(face.lineIds),
        lineIds: face.lineIds,
        jointIds: face.jointIds,
        area: face.area,
        exposed: face.lineIds.some((lineId) => hullLineIds.has(lineId)),
    }));
}
/**
 * The lines that make up the outside of the ship.
 *
 * Anything wound clockwise bounds the outside; a disconnected ship has more than
 * one such face, hence a set rather than a single boundary.
 *
 * ── Not the same question as `Compartment.exposed` ──────────────────────────
 *
 * A compartment is exposed when *any* of its walls is one of these. A **line**
 * is on the skin when it is one of these itself, and the two come apart exactly
 * where it matters: an internal bulkhead between two rooms that both touch the
 * outside belongs to two exposed compartments and is not on the skin.
 *
 * That distinction is what decides whether a thruster bolted to it can push
 * against anything — see `activeComponents`.
 */
export function hullLines(graph) {
    return outerLines(traceFaces(graph));
}
function outerLines(faces) {
    const ids = new Set();
    for (const face of faces) {
        if (face.area < -EPSILON) {
            for (const lineId of face.lineIds)
                ids.add(lineId);
        }
    }
    return ids;
}
/** Total enclosed area. This is the ship's "surface area" for component capacity. */
export function totalSurfaceArea(compartments) {
    return compartments.reduce((sum, compartment) => sum + compartment.area, 0);
}
/** Ray casting. Points exactly on an edge are not guaranteed either way. */
function pointInPolygon(point, polygon) {
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const a = polygon[i];
        const b = polygon[j];
        if (!a || !b)
            continue;
        const straddles = a.y > point.y !== b.y > point.y;
        if (!straddles)
            continue;
        const crossingX = ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y) + a.x;
        if (point.x < crossingX)
            inside = !inside;
    }
    return inside;
}
function polygonOf(compartment, joints) {
    const points = [];
    for (const jointId of compartment.jointIds) {
        const joint = joints.get(jointId);
        if (joint)
            points.push({ x: joint.x, y: joint.y });
    }
    return points;
}
/**
 * Work out which compartment each component sits in.
 *
 * When compartments nest, the smallest containing one wins — a component is
 * held by the walls immediately around it, not by an outer ring that also
 * happens to contain it.
 */
export function placeComponents(graph, compartments) {
    const joints = indexJoints(graph);
    const polygons = compartments.map((compartment) => ({
        compartment,
        polygon: polygonOf(compartment, joints),
    }));
    const byComponent = new Map();
    const floating = [];
    for (const component of graph.components) {
        let best;
        for (const { compartment, polygon } of polygons) {
            if (polygon.length < 3)
                continue;
            if (!pointInPolygon(component.position, polygon))
                continue;
            if (!best || compartment.area < best.area)
                best = compartment;
        }
        if (best)
            byComponent.set(component.id, best.id);
        else
            floating.push(component);
    }
    return { byComponent, floating };
}
//# sourceMappingURL=compartments.js.map